<template>
  <v-container wrap>
    <div v-if="loading" class="text-center">
      <circles-loader></circles-loader>
    </div>
    <v-card v-else class="pa-3" width="100%" style="margin-bottom: 20px" outlined elevation="0">
      <v-card-actions v-if="documentContents && availableLanguages && availableLanguages.length > 1">
        <template v-for="l in availableLanguages">
          <v-btn elevation="0" @click="switchLang(l)" :color="l == selectedLang ? 'pink' : 'primary'" fab small dark
            :key="templateId + '_' + l">
            {{ l }}
          </v-btn>
        </template>
      </v-card-actions>
      <div class="text-sm-left" style="margin-bottom:10px">
        <h4>{{ $d(today, 'short', selectedLang) }}</h4>
      </div>
      <v-card-text class="text-center text-h5" v-if="academyDocumentTemplate && academyDocumentTemplate.name">
        <h2>{{ academyDocumentTemplate.name }}</h2>
      </v-card-text>
      <div v-if="documentContents" v-html="documentContents" class="text-sm-left pa-2">
      </div>
      <div class="text-left" v-if="academyDocumentTemplate && isSurvey && academyDocumentTemplate.questions">
        <div style="margin-left:15px"><span class="required">*</span> <span class="supporting-text">{{
          $t('message.required')
            }}</span></div>
        <template v-for="(i, index) in academyDocumentTemplate.questions">
          <v-card :key="'question_' + index" class="elevation-0 question-card">
            <v-card-title>{{ index + 1 }} - {{ i.question }} <span v-if="i.required"
                class="required">*</span></v-card-title>
            <v-card-text class="answer-box">
              <span v-if="i.answerType == 'boolean'">
                <v-radio-group v-model="i.answerBoolean">
                  <v-radio :label="$t('message.true')" :value="true"></v-radio>
                  <v-radio :label="$t('message.false')" :value="false"></v-radio>
                </v-radio-group>
              </span>
              <span v-else-if="i.answerType == 'multiple_choice'">
                <v-radio-group v-model="i.answer">
                  <template v-for="(ao, index2) in i.answerOptions">
                    <v-radio :key="i._id + 'answer_option_' + index2" :label="ao" :value="ao" v-if="ao != ''"></v-radio>
                  </template>
                </v-radio-group>
              </span>
              <span v-else>
                <v-text-field v-model="i.answer" :label="$t('message.your_answer')"></v-text-field>
              </span>
            </v-card-text>
          </v-card>
        </template>
      </div>
      <v-card-text v-if="academyDocumentTemplate && academyDocumentTemplate.extraFields">
        <v-list>
          <template v-for="(field, index) in academyDocumentTemplate.extraFields">
            <v-text-field :key="'extra_field_' + index"
              :label="field.fieldName + (field.required ? ' (' + $t('message.required').toLowerCase() + ')' : '')"
              v-model="field.value"></v-text-field>
            <v-divider v-if="index + 1 < academyDocumentTemplate.extraFields.length" :key="index"></v-divider>
          </template>
        </v-list>
      </v-card-text>
      <div v-if="messages" class="messages-area text-center" v-html="messages" style="padding:5px">
      </div>
      <div class="text-center" v-if="processing">
        <v-progress-circular indeterminate color="primary" size="80">
        </v-progress-circular>
      </div>
      <div v-if="pendingDocuments && pendingDocuments.length > 0">
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" large block color="primary" @click="loadNextDocument()">{{
            $t('message.complete_next_document')
          }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-card-actions>
          <v-btn block elevation="0" color="secondary" @click="cancel()">{{ $t('message.later') }}</v-btn>
        </v-card-actions>
      </div>
      <div v-else-if="!isSurvey">
        <v-card-title>
          <v-text-field style="margin-right:20px" :label="$t('message.name', selectedLang)"
            v-model="participantName"></v-text-field>
          <v-text-field :disabled="true" :label="$t('message.email', selectedLang)"
            v-model="participantEmail"></v-text-field>
        </v-card-title>
        <v-card-title>
          <v-text-field style="margin-right:20px" :label="$t('message.guardian', selectedLang)"
            v-model="guardianName"></v-text-field>
          <v-text-field :label="$t('message.todays_date') + ' (' + $t('message.yyyy_mm_dd', selectedLang) + ')'"
            v-model="date"></v-text-field>
        </v-card-title>
        <div class="text-center">
          <Vue-Signature style="border: 1px solid black; margin-left:auto; margin-right:auto;" :w="'700px'" :h="'150px'"
            ref="waiverSignature" :sigOption="signatureOptions"></Vue-Signature>
        </div>
        <v-card-actions>
          <v-btn :loading="processing" :disabled="!participantName || !participantEmail || !date" color="primary"
            elevation="0" large block @click="submitWaver()">
            {{ $t('message.submit', [selectedLang]) }}
          </v-btn>
        </v-card-actions>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" outlined elevation="0" @click="clearSignature()">
            {{ $t('message.clear_signature') }}
          </v-btn>
          <v-btn color="secondary" elevation="0" @click="cancel()">
            {{ $t('message.cancel', selectedLang) }}
          </v-btn>
        </v-card-actions>
      </div>
      <div v-else-if="isSurvey">
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn large elevation="0" color="primary" @click="submitWaver()">
            {{ $t('message.submit', [selectedLang]) }}
          </v-btn>
          <v-btn large elevation="0" color="error" @click="cancel()">
            {{ $t('message.cancel', selectedLang) }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import Api from '@/services/Api';
import VueSignature from "vue-signature";

var moment = require("moment");

export default {
  name: 'DocumentTemplate',
  data() {
    return {
      loading: true,
      templateId: this.$route.params.templateId,
      memberId: this.$route.params.memberId,
      messages: null,
      participantEmail: null,
      participantName: null,
      pendingDocuments: null,
      guardianName: null,
      date: null,
      availableLanguages: [],
      isSurvey: false,
      selectedLang: null,
      today: new Date(),
      processing: false,
      signatureOptions: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)"
      },
      documentContents: this.$t('message.loading'),
      academyDocumentTemplate: null
    }
  },
  components: {
    VueSignature,
    CirclesLoader
  },
  created() {
    //Get document and prefill email and ID hidden in BG
    if (!this.$store.state.tabletManagedAcademy) {
      this.navigateTo({ name: 'login' });
    } else {
      this.date = moment(new Date()).format("YYYY-MM-");
      this.loadDocument();
    }
  },
  methods: {
    loadDocument() {
      Api().get('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/template/' + this.templateId + '/member/' + this.memberId)
        .then(response => {
          const data = response.data;
          this.loading = false;
          if (data) {
            this.participantEmail = data.member.email;
            this.academyDocumentTemplate = data.template;
            if (data.template && data.template.survey)
              this.isSurvey = true;
            this.availableLanguages.push(data.template.lang);
            for (const t in data.template.contentTransaltions) {
              this.availableLanguages.push(t);
            }
            this.switchLang(data.template.lang);
            this.pendingDocuments = null;
          }
        })
        .catch(e => {
          this.loading = false;
          if (e)
            this.documentContents = '<span style="color:red;font-size:1.5em;text-align:center">Error</span>';
        })
    },
    cancel() {
      this.navigateTo({ name: 'checkin' });
    },
    isToday() {
      try {
        const day = moment(this.date, "YYYY-MM-DD");
        const today = moment();
        if (day.isSame(today, "day") && day.isSame(today, "date")) return true;
        else return false;
      } catch (e) {
        return false;
      }
    },
    clearSignature() {
      var _this = this;
      _this.$refs.waiverSignature.clear();
    },
    switchLang(lang) {
      this.selectedLang = lang;
      if (lang == this.academyDocumentTemplate.lang) {
        this.documentContents = this.academyDocumentTemplate.contents;
      } else {
        this.documentContents = this.academyDocumentTemplate.contentTransaltions[lang];
      }
    },
    loadNextDocument() {
      if (this.pendingDocuments && this.pendingDocuments[0]) {
        this.loading = true;
        this.messages = null;
        this.isSurvey = false;
        this.templateId = this.pendingDocuments[0];
        this.loadDocument();
      }
    },
    submitWaver() {
      //Add succeess message and close one second after
      var _this = this;
      var pngImage = null;
      if (!this.isSurvey) {
        pngImage = _this.$refs.waiverSignature.save();
        if (_this.$refs.waiverSignature.isEmpty()) {
          this.messages = '<span style="color:red;font-size:1.5em">' + this.$t('message.please_sign') + '</span>';
          return;
        }
        if (this.academyDocumentTemplate.extraFields) {
          for (const field of this.academyDocumentTemplate.extraFields) {
            if (field.required && !field.value) {
              this.messages = '<span style="color:red;font-size:1.5em">' + this.$t('message.complete_required_parameters') + '</span>';
              return;
            }
          }
        }
      } else {
        //Required parameters for surveys
        const template = this.academyDocumentTemplate;
        if (template && template.questions && template.questions.length > 0) {
          for (const q of template.questions) {
            if (q.required) {
              if (q.answerType == 'text' && !q.answer) {
                this.messages = '<span style="color:red;font-size:1.5em">' + this.$t('message.complete_required_parameters') + '</span>';
                return;
              } else if (q.answerType == 'boolean' && typeof q.answerBoolean == 'undefined') {
                this.messages = '<span style="color:red;font-size:1.5em">' + this.$t('message.complete_required_parameters') + '</span>';
                return;
              }
            }
          }
        }
      }
      if (this.isSurvey || this.isToday()) {
        //Post data
        const postData = {
          name: this.participantName,
          guardianName: this.guardianName,
          email: this.participantEmail,
          userDate: this.date,
          initials: this.initials,
          lang: this.selectedLang,
          contents: this.documentContents,
          extraFieldValues: this.academyDocumentTemplate.extraFields,
          signature: pngImage,
          questions: this.academyDocumentTemplate.questions,
        };
        this.processing = true;
        Api().post('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/template/' + this.templateId + '/member/' + this.memberId, postData)
          .then(response => {
            const data = response.data;
            if (data) {
              const vm = this;
              if (!this.isSurvey)
                this.clearSignature();
              try {
                let messages = '<div style="color:green;font-size:1.5em">' + this.$t('message.doc_submitted', this.selectedLang) + '</div>';
                this.academyDocumentTemplate = null;
                if (data.missingDocuments && data.missingDocuments.length > 0) {
                  this.messages = messages + '<div style="color:#EC7063;font-size:1.3em">' + this.$t('message.more_documents', [data.missingDocuments.length]) + '</div>';
                  this.pendingDocuments = data.missingDocuments;
                  this.availableLanguages = [];
                  this.documentContents = null;
                  this.processing = false;
                } else {
                  this.messages = messages;
                  setTimeout(function () {
                    vm.cancel();
                  }, 2000);
                }
              } catch (e) {
                console.log(e);
                this.messages = '<span style="color:red;font-size:1.5em">Error</span>';
                this.processing = false;
              }
            }
          })
          .catch(e => {
            if (e)
              this.messages = '<span style="color:red;font-size:1.5em">Error</span>';
          })
      } else {
        //Show Message
        this.messages = '<span style="color:red;font-size:1.5em">' + this.$t('message.not_today') + '</span>';
      }
    }
  }
}
</script>
